import { configureStore, createDynamicMiddleware } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import {
  persistReducer,
  persistStore,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from 'redux-persist';
import uiReducer from './slices/uiSlice';
import modalReducer from './slices/modalSlice';
import findReplaceReducer from './slices/findReplaceSlice';
import { persistConfig } from './persistConfig';
import { createSyncMiddleware, initStateListener } from './syncConfig';
import { createReducerManager, StoreWithReducerManager } from './dynamicStore';
import { baseApi } from './baseApi';
import { featureRegistryStaticReducers } from './features';
import { setStore, setPersistor } from './features/storeReference';
import { initializeDynamicPersistStore, updatePersistor } from './dynamicPersistStore';
import userReducer from '../features/user/store/slice';
import { userAuthErrorMiddleware } from '../features/user/store/middleware';
import { runActiveEncounterIdMigration } from './migrations/activeEncounterIdMigration';
import { runScopedPersistMigration } from './migrations/scopedPersistMigration';
import { workflowPurgeMiddleware } from '../features/encounters/store/workflowPurgeMiddleware';
import { setOwner, createOwner, type Owner } from './owner';

// All redux-persist actions should be ignored by the serializableCheck middleware
export const serializableCheckIgnoredActions = [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER];

// Define static reducers - these are always loaded (core functionality only)
const staticReducers = {
  [baseApi.reducerPath]: baseApi.reducer,
  ui: uiReducer,
  modals: modalReducer,
  findReplace: findReplaceReducer,
  user: userReducer, // Add user reducer as static to prevent race conditions
  ...featureRegistryStaticReducers, // Add feature registry reducer
};

// The setupListeners call on line 72 will ensure RTK Query reacts to changes

// Create a reducer manager to handle dynamic reducers
const reducerManager = createReducerManager(staticReducers);

// Create dynamic middleware instance for Redux Toolkit 2.0
export const dynamicMiddleware = createDynamicMiddleware();

// Create a persisted root reducer function that works with dynamic reducers
const rootReducer = (state: any, action: any) => {
  // Get the current state of all reducers (including dynamically added ones)
  const result = reducerManager.reduce(state, action);

  // Debug: log when state changes occur
  if (process.env.NODE_ENV !== 'production' && action.type?.includes('actions/ui')) {
    console.log(`🔄 Root reducer processing action: ${action.type}`, {
      previousState: state?.actionUI,
      newState: result?.actionUI,
      allKeys: Object.keys(result || {}),
    });
  }

  return result;
};

// Create a persisted reducer with the configuration
// This will handle persistence for the entire store, including dynamically added reducers
const persistedReducer = persistReducer(persistConfig, rootReducer);

// Create the sync middleware for cross-tab communication
const syncMiddleware = createSyncMiddleware();

// Create a function to get all middleware (static + dynamic)
const getMiddleware = (getDefaultMiddleware: any) => {
  return getDefaultMiddleware({
    serializableCheck: {
      ignoredActions: serializableCheckIgnoredActions,
    },
  }).concat(
    baseApi.middleware, // Add the base API middleware
    // adminV2Api.middleware is not needed since adminV2Api extends baseApi
    // userApi.middleware is not needed since userApi extends baseApi
    userAuthErrorMiddleware, // Add the user auth error middleware
    workflowPurgeMiddleware, // Add targeted workflow purge middleware
    syncMiddleware,
    dynamicMiddleware.middleware, // Add dynamic middleware for lazy-loaded features
  );
};

// Create the store with proper middleware configuration
const baseStore = configureStore({
  reducer: persistedReducer,
  middleware: getMiddleware,
  devTools: process.env.NODE_ENV !== 'production',
});

export const store = baseStore as typeof baseStore & StoreWithReducerManager;

// Attach the reducer manager and dynamic middleware to the store
store.reducerManager = reducerManager;
store.dynamicMiddleware = dynamicMiddleware;

// DO NOT create persistor immediately - delay until owner is available
// This prevents rehydration from happening before scoped persistence is set up
export let persistor: ReturnType<typeof persistStore> | null = null;

// Initialize the state listener for cross-tab communication
initStateListener(store);

// Enable refetchOnFocus, refetchOnReconnect, and other RTK Query behaviors
// This ensures queries will refetch when their parameters change
setupListeners(store.dispatch);

// Import the RootState interface from types.ts
import { RootState as RootStateInterface } from './types';

// Make the store available to feature hooks
setStore(store);
// Note: persistor is set later in initializeScopedPersistence

// Initialize dynamic persist store manager early (before features auto-register)
// Pass null for persistor - it will be updated later in initializeScopedPersistence
initializeDynamicPersistStore(store, null, staticReducers);

// Run one-time migration for legacy activeEncounterId
// TODO(2025-11-20): Remove this after 30 days - see activeEncounterIdMigration.ts
runActiveEncounterIdMigration(store);

/**
 * Initialize scoped persistence for authenticated user
 *
 * This should be called after getUserInfo completes to set up:
 * 1. Owner context (userId, env)
 * 2. Create persistor with scoped configs
 * 3. Scoped persistence migration (legacy → scoped keys)
 *
 * @param userId - User ID from getUserInfo
 */
export function initializeScopedPersistence(userId: string): void {
  try {
    // 1. Create and set owner FIRST (before migration and persistor)
    const owner: Owner = createOwner(userId);
    setOwner(owner);

    // 2. Run scoped persistence migration BEFORE creating persistor
    // This ensures migrated data is in place before rehydration begins
    runScopedPersistMigration(owner);

    // 3. Now create persistor - will rehydrate from the migrated scoped keys
    if (persistor === null) {
      persistor = persistStore(store);
      setPersistor(persistor);

      // 4. Update the persistor reference in dynamic persist store manager
      updatePersistor(persistor);
    }
  } catch (error) {
    console.error('[ScopedPersist] ❌ Failed to initialize scoped persistence:', error);
    // Don't throw - app should still work even if scoped persistence fails
  }
}

// Re-export the types but use ReturnType to ensure correct typing
export type RootState = RootStateInterface;
export type AppStore = typeof store;
export type AppDispatch = typeof store.dispatch;
